<template>
  <div class="px-2">
    <div class="p-5 pb-0">
      <div>
        <div>
          <v-list class="w-full" v-for="(label, key, index) in filteredUseCases" :key="key">
            <v-row class="items-center mb-2">
              <div class="flex items-center justify-between w-full">
                <p class="text-md font-semibold mr-4">{{ index + 1 }}.</p>
                <div class="flex items-center">
                  <p v-if="key.toUpperCase() === 'COLLECTION'" class="text-md font-semibold">
                    {{ getDisplayName(key) }} <span class="text-red-500">(Deprecated)</span>
                  </p>
                  <p v-else class="text-md font-semibold">
                    {{ getDisplayName(key) }}
                    <span class="text-gray-500" v-if="extractAllowedUseCaseName(key)?.name"
                      >({{ extractAllowedUseCaseName(key)?.name }}
                      <v-icon>{{ extractAllowedUseCaseName(key)?.icon }}</v-icon
                      >)
                    </span>
                  </p>
                  <v-menu v-model="popoverMenu[key]" :close-on-content-click="false" offset-y right>
                    <!-- Popover for editing display name -->
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        v-bind="attrs"
                        v-on="on"
                        @click.stop="openPopover(key)"
                        v-tooltip="{ text: 'Edit Display Name' }"
                        class="mb-4"
                      >
                        <v-icon small>mdi-pencil</v-icon>
                      </v-btn>
                    </template>

                    <v-card min-width="300">
                      <v-card-text class="pa-3">
                        <v-text-field
                          v-model="tempDisplayNames[key]"
                          label="Display Name"
                          outlined
                          dense
                          hide-details
                        >
                          <template v-slot:append>
                            <v-icon
                              @click="toggleIconSelector(key)"
                              class="cursor-pointer"
                              :loading="iconButtonLoading[key]"
                            >
                              {{ selectedIcons[key] || 'mdi-help-circle' }}
                            </v-icon>
                          </template>
                        </v-text-field>

                        <v-expand-transition>
                          <div v-if="showIconList[key]" class="mt-3">
                            <v-text-field
                              v-model="iconSearchTerm"
                              placeholder="Search icons..."
                              prepend-inner-icon="mdi-magnify"
                              outlined
                              dense
                              hide-details
                              class="mb-2"
                            ></v-text-field>

                            <div class="icon-grid-container custom-scrollbar">
                              <div v-if="loadingIcons" class="d-flex justify-center pa-4">
                                <v-progress-circular
                                  indeterminate
                                  color="primary"
                                  size="24"
                                ></v-progress-circular>
                              </div>
                              <template v-else>
                                <div class="grid grid-cols-5 gap-2">
                                  <div
                                    v-for="icon in filteredIcons"
                                    :key="icon.icon"
                                    class="icon-item d-flex flex-column align-center justify-center pa-1 rounded cursor-pointer"
                                    :class="{ 'selected-icon': selectedIcons[key] === icon.icon }"
                                    @click="selectIcon(key, icon)"
                                    v-tooltip="icon.text"
                                  >
                                    <v-icon
                                      :color="selectedIcons[key] === icon.icon ? 'primary' : ''"
                                      >{{ icon.icon }}</v-icon
                                    >
                                  </div>
                                </div>
                                <div v-if="hasMoreIcons" class="d-flex justify-center mt-2">
                                  <v-btn x-small text color="primary" @click="loadMoreIcons"
                                    >Load More</v-btn
                                  >
                                </div>
                              </template>
                            </div>
                          </div>
                        </v-expand-transition>

                        <div class="d-flex justify-space-between mt-2">
                          <v-btn small class="normal-case" text @click="closePopover(key)"
                            >Cancel</v-btn
                          >
                          <v-btn
                            small
                            class="normal-case"
                            color="primary"
                            @click="saveDisplayName(key)"
                          >
                            <v-icon left small>mdi-content-save</v-icon>
                            Save
                          </v-btn>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-menu>
                </div>

                <!-- </div> -->
              </div>
            </v-row>
            <v-list-item-group>
              <v-row>
                <v-list-item class="space-x-4 border border-blue-200 mb-8">
                  <v-col cols="11">
                    <v-chip
                      v-for="(chip, index) in extractSubCases(key)"
                      :key="chip.value || `${key}-${index}`"
                      label
                      small
                      class="border border-black mr-2 mb-1"
                    >
                      <span class="pr-2 text-black">
                        {{ chip.text }}
                      </span>

                      <v-icon
                        v-if="canEditOrDeleteSubCase(key, chip)"
                        small
                        @click="removeSubCaseChip(chip, index, key, label)"
                        color="red"
                        class="ml-2"
                        v-tooltip="{
                          text: 'Delete'
                        }"
                      >
                        $delete
                      </v-icon>

                      <v-icon
                        v-if="canEditOrDeleteSubCase(key, chip)"
                        small
                        @click="editItem(chip, label, index, key)"
                        color="green"
                        class="ml-2"
                        v-tooltip="{
                          text: 'Edit'
                        }"
                      >
                        $edit
                      </v-icon>

                      <!-- <v-icon
                        v-if="canEditOrDeleteSubCase(key, chip)"
                        small
                        @click="cloneSubCaseChip(chip, index, key)"
                        color="blue"
                        class="ml-2"
                        v-tooltip="{
                          text: 'Clone'
                        }"
                      >
                        mdi-content-copy
                      </v-icon> -->
                      <v-icon
                        v-else
                        small
                        @click="
                          $toast.error('You dont have sufficient permissions to view this subcase')
                        "
                        color="grey"
                        class="ml-2"
                        v-tooltip="{
                          text: 'You dont have sufficient permissions to view this subcase'
                        }"
                      >
                        mdi-lock
                      </v-icon>
                    </v-chip>
                  </v-col>

                  <v-col cols="1">
                    <div class="flex justify-end">
                      <v-btn
                        text
                        :color="$vuetify.theme.currentTheme.primary"
                        class="normal-case"
                        @click="handleOrgMapping(key, label)"
                        >Add Subcase</v-btn
                      >
                    </div>
                  </v-col>
                </v-list-item>
              </v-row>
            </v-list-item-group>
          </v-list>
        </div>
      </div>
    </div>
    <v-dialog v-model="openOrgMapping" max-width="63rem" persistent>
      <OrgMapping
        :useCase="mappedUseCase"
        :useCaseLabel="mappedUseCaseLabel"
        :baseMapping="baseCaseMapping"
        @cancel="onCancelOrgMapping"
        @orgMapKeys="handleMapKeys"
        @orgSubCaseMapKeys="handleSubCaseMapKeys"
        :enabledSubCases="enabledSubCases"
        :editSubCase="editSubCase"
        :cloneSubCase="cloneSubCase"
        :isWhatsAppEnabled="!!receivedData?.applications?.whatsapp?.waSystemToken"
        :allowedQuestionsTypes="receivedData?.allowedQuestionTypes"
        :allowedQuestionsTypesCustomerFeedback="receivedData?.allowedQuestionTypesCustomerFeedback"
        :showMappingDetails="mappedUseCase?.toLowerCase() === 'collection'"
      />
    </v-dialog>
    <AlertPopUp
      :showConfirmationDialog="showDeleteSubCaseDialog"
      :confirmationTitle="'Confirm Delete'"
      :confirmationMessage="getDeleteConfirmationMessage"
      :confirmationActionText="'DELETE'"
      :confirmationActionColor="'red'"
      :performAction="deleteSubCase"
      :cancel="cancel"
      :loading="isCheckingCount"
      :hasDataCount="dataCountResult && dataCountResult.count > 0"
      :dataCount="dataCountResult?.count"
    />
  </div>
</template>

<script>
import OrgMapping from '../OrgMapping'
import AlertPopUp from '@/components/AlertPopUp'

import { convertToTitleCase } from '@/utils/common'
import { cloneDeep } from 'lodash'
import { dynamicRequestPath } from '@/utils/workforce/constants'
// import CustomSwitch from './CustomSwitch.vue'
const defaultOrgKeys = [
  {
    text: 'Case No (Unique Identifier)',
    key: 'caseNo',
    value: '',
    visibility: true,
    rules: [(v) => !!v || 'Case No is required'],
    required: true
  },
  {
    text: 'Type of loan',
    key: 'typeOfLoan',
    value: '',
    visibility: true,
    required: false
  },
  {
    text: 'Bucket',
    key: 'bkt',
    value: '',
    visibility: true,
    required: false
  },
  {
    text: 'Product Info',
    key: 'productInfo',
    value: '',
    visibility: true,
    required: false
  },
  {
    text: 'POS (Number)',
    key: 'pos',
    value: '',
    visibility: true,
    required: false
  },
  {
    text: 'EMI Amount (Number)',
    key: 'emiAmount',
    value: '',
    visibility: true,
    required: false
  },
  {
    text: 'Tenure (Number)',
    key: 'tenure',
    value: '',
    visibility: true,
    required: false
  },
  {
    text: 'Task Title',
    key: 'taskTitle',
    value: '',
    visibility: true,
    rules: [(v) => !!v || 'Task Title is required'],
    required: true
  },
  {
    text: 'Task Description',
    key: 'taskDescription',
    value: '',
    visibility: true,
    required: false
  },
  {
    text: 'Assigned Person',
    key: 'assignedTo',
    value: '',
    visibility: true,
    required: false
  },
  {
    text: 'EMI Start Date (Date)',
    key: 'emiStartDate',
    value: '',
    visibility: false,
    required: false
  },
  {
    text: 'EMI End Date (Date)',
    key: 'emiEndDate',
    value: '',
    visibility: false,
    required: false
  },
  {
    text: 'Next EMI Due Date (Date)',
    key: 'nextEmiDate',
    value: '',
    visibility: false,
    required: false
  }
]
export default {
  data() {
    return {
      use_cases: {},
      selectedUseCases: [],
      enabledSubCases: [],
      selectedChip: null,
      showDeleteSubCaseDialog: false,
      dataCountResult: null,
      isCheckingCount: false,
      openOrgMapping: false,
      mappedUseCase: '',
      mappedUseCaseLabel: '',
      editSubCase: null,
      cloneSubCase: null,
      qrCode: '',
      orgData: this.$storage.getUniversal('orgData'),
      orgCode: this.$storage.getUniversal('organization_code'),
      userOrg: this.$storage.getUniversal('user'),
      qrValue: `${process.env.VUE_APP_BASE_URL}/mark-attendance?orgCode=${this.orgCode}`,
      drawer: false,
      useCasesMapping: {},
      baseCaseMapping: {},
      allSubCases: [],
      // Display name properties
      displayNames: {},
      popoverMenu: {},
      tempDisplayNames: {},
      // Icon selector properties
      // Icon selector properties
      showIconList: {},
      iconSearchTerm: '',
      visibleIconsCount: 60,
      selectedIcons: {},
      currentKey: '',
      iconButtonLoading: {},
      apiIcons: [],
      iconsLoaded: false,
      loadingIcons: false,
      allowedUseCases: []
    }
  },
  components: {
    OrgMapping,
    AlertPopUp
    // CustomSwitch
  },
  props: {
    receivedData: {
      type: Object,
      default: null
    }
  },
  watch: {
    receivedData(newVal) {
      if (newVal !== null) {
        this.populateFields(newVal)
      }
    }
  },
  methods: {
    // Display name methods
    getDisplayName(key) {
      return this.convertTitle(key)
    },
    extractAllowedUseCaseName(key) {
      const allowedUseCase = this.allowedUseCases.find((u) => u.useCase === key)
      return { name: allowedUseCase?.navName, icon: allowedUseCase?.navIcon }
    },
    openPopover(key) {
      const savedData = this.extractAllowedUseCaseName(key)
      this.$set(this.tempDisplayNames, key, savedData.name || this.getDisplayName(key))
      if (savedData.icon) {
        this.$set(this.selectedIcons, key, savedData.icon)
      }
      this.$set(this.popoverMenu, key, true)
    },
    closePopover(key) {
      this.$set(this.popoverMenu, key, false)
      this.$delete(this.tempDisplayNames, key)
    },

    // Icon selector methods
    // Icon selector methods
    toggleIconSelector(key) {
      // If closing
      if (this.showIconList[key]) {
        this.$set(this.showIconList, key, false)
        return
      }

      // If opening, close others first
      this.showIconList = {}
      this.$set(this.showIconList, key, true)

      this.currentKey = key
      this.iconSearchTerm = ''
      this.visibleIconsCount = 60

      // Set loading state for this specific button
      this.$set(this.iconButtonLoading, key, true)

      // Fetch icons if needed
      if (!this.iconsLoaded && !this.loadingIcons) {
        this.fetchApiIcons()
          .then(() => {
            this.$set(this.iconButtonLoading, key, false)
          })
          .catch(() => {
            this.$set(this.iconButtonLoading, key, false)
          })
      } else if (this.loadingIcons) {
        // Wait for existing loading
        const checkLoading = () => {
          if (!this.loadingIcons) {
            this.$set(this.iconButtonLoading, key, false)
          } else {
            setTimeout(checkLoading, 100)
          }
        }
        checkLoading()
      } else {
        this.$set(this.iconButtonLoading, key, false)
      }
    },

    selectIcon(key, icon) {
      this.$set(this.selectedIcons, key, icon.icon)
      // We don't close the list immediately so user can try other icons
      // But we emit update immediately? No, wait for Save button.
      // The original code emitted updateDisplayNames in onIconSelected.
      // But here we are inside the popover which has a Save button.
      // The Save button calls saveDisplayName(key).
      // saveDisplayName uses this.selectedIcons[key].
      // So we just update the local state.
    },

    loadMoreIcons() {
      this.visibleIconsCount += 60
    },

    async fetchApiIcons() {
      // Early return if icons are already loaded or currently loading
      if (this.iconsLoaded || this.loadingIcons) {
        return Promise.resolve()
      }

      this.loadingIcons = true
      try {
        const res = await this.$axios.get('/mdi-icons')
        if (res.data && res.data.data && Array.isArray(res.data.data.i)) {
          this.apiIcons = res.data?.data?.i.map((icon) => {
            return {
              text: icon.n,
              icon: `mdi-${icon.n}`
            }
          })
          this.iconsLoaded = true // Mark as loaded to prevent future calls
        } else {
          throw new Error('Invalid API response format')
        }
      } catch (error) {
        console.error('Error fetching API icons:', error)
        this.$toast.error('Failed to load icons. Please try again.')
        throw error // Re-throw to handle in calling function
      } finally {
        this.loadingIcons = false
      }
    },

    async saveDisplayName(key) {
      const displayName = this.tempDisplayNames[key]
      if (displayName && displayName.trim()) {
        const payload = {
          useCase: key,
          navName: displayName.trim(),
          navIcon: this.selectedIcons[key]
        }
        try {
          await this.$axios.post('/orgSetup/allowed-use-cases', payload)

          // Update local allowedUseCases immediately
          const existingIndex = this.allowedUseCases.findIndex((u) => u.useCase === key)
          if (existingIndex !== -1) {
            this.$set(this.allowedUseCases, existingIndex, {
              ...this.allowedUseCases[existingIndex],
              navName: payload.navName,
              navIcon: payload.navIcon
            })
          } else {
            this.allowedUseCases.push({
              useCase: key,
              navName: payload.navName,
              navIcon: payload.navIcon
            })
          }
        } catch (e) {
          console.log(e)
        }
        this.$set(this.displayNames, key, displayName.trim())
        this.$emit('updateDisplayNames', {
          ...this.displayNames,
          icons: { ...this.selectedIcons }
        })
      }
      this.closePopover(key)
    },
    async fecthAllowedUseCases() {
      try {
        const res = await this.$axios.get(`/orgSetup/allowed-use-cases`)
        this.allowedUseCases = res.data?.data || []
        // this.use_cases = this.allowedUseCases.reduce((acc, useCase) => {
        //   if (useCase.useCase) {
        //     acc[useCase.useCase] = useCase
        //   }
        //   return acc
        // }, {})
      } catch (error) {
        console.log(error)
      }
    },
    async fetchAllUseCases() {
      try {
        const res = await this.$axios.get(`/orgSetup/extend-usecase`)
        this.allSubCases = res.data?.data
      } catch (error) {
        console.log(error)
      }
    },
    async fetchStaticUseCaseData() {
      try {
        const response = await this.$axios.get('/orgSetup/loan-management-field-mapping/data')
        const { base, ...remainingUseCases } = response.data?.data
        this.useCasesMapping = remainingUseCases
        this.baseCaseMapping = base

        this.use_cases = { base, ...this.useCasesMapping }
        this.populateFields(this.receivedData)
      } catch (error) {
        console.error('Error fetching static loan data:', error)
      }
    },
    convertTitle(str) {
      return convertToTitleCase(str)
    },
    extractSubCases(useCaseKey) {
      const subCasesForUseCase = this.allSubCases.filter(
        (subCase) => subCase.useCase === useCaseKey.toLowerCase()
      )

      const allSubCases = subCasesForUseCase.map((subCase) => {
        return { text: subCase.name, value: subCase._id, source: 'api' }
      })
      // const enabledSubCasesForUseCase = this.enabledSubCases?.find(
      //   (sub) => sub.useCase === useCaseKey.toLowerCase()
      // )

      // if (!enabledSubCasesForUseCase || !enabledSubCasesForUseCase.subCase) {
      //   return allSubCases
      // }
      // const enabledSubCasesArray = enabledSubCasesForUseCase.subCase.map((sub) => {
      //   return {
      //     text: sub.name,
      //     value: sub._id || `enabled-${sub.name}`,
      //     source: 'enabled',
      //     branch: sub.branch
      //   }
      // })

      const combinedSubCases = []
      allSubCases.forEach((apiSubCase) => {
        if (!combinedSubCases.some((enabledSubCase) => enabledSubCase.text === apiSubCase.text)) {
          combinedSubCases.push(apiSubCase)
        }
      })
      return combinedSubCases
    },
    canEditOrDeleteSubCase(useCaseKey, subCaseName) {
      return true
      const { branch, role } = this.userOrg || {}
      const enabledSubCasesForUseCase = this.enabledSubCases?.find(
        (sub) => sub.useCase === useCaseKey.toLowerCase()
      )

      if (role?.includes('WFM_ADMIN') || !branch) {
        return true
      }

      const matchingSubCase = enabledSubCasesForUseCase?.subCase.find(
        (sub) => sub.name === subCaseName && sub.branch === branch
      )

      return !!matchingSubCase
    },
    async removeSubCaseChip(chipName, index, key) {
      if (key === 'COLLECTION') {
        const enabledSubCasesForUseCase = this.enabledSubCases?.find(
          (sub) => sub.useCase === key.toLowerCase()
        )
        enabledSubCasesForUseCase.subCase.splice(index, 1)
        this.$emit('subCases', this.enabledSubCases)
        return
      }
      this.mappedUseCase = key
      this.selectedChip = chipName

      // Check data count before showing delete dialog
      await this.checkDataCount(chipName)

      this.showDeleteSubCaseDialog = true
      this.$emit('subCases', this.enabledSubCases)
    },
    cloneSubCaseChip(chipName, indx, key) {
      for (let i = 0; i < this.enabledSubCases.length; i++) {
        const subCase = this.enabledSubCases[i]
        const selectedSubCase = subCase.subCase.find((item) => item.name === chipName)

        if (selectedSubCase) {
          this.cloneSubCase = cloneDeep(selectedSubCase)
          this.cloneSubCase.cloneIndex = indx
        }
      }

      this.openOrgMapping = true
      this.mappedUseCase = key
    },
    async checkDataCount(chipName) {
      try {
        if (!chipName.value) {
          this.dataCountResult = null
          return
        }

        this.isCheckingCount = true
        const response = await this.$axios.get(`/orgSetup/extend-usecase/count/${chipName.value}`)
        this.dataCountResult = response.data
      } catch (error) {
        console.error('Error checking data count:', error)
        this.dataCountResult = null
      } finally {
        this.isCheckingCount = false
      }
    },
    async deleteSubCase() {
      try {
        if (!this.selectedChip) {
          this.$toast.error('No case selected for deletion')
          return
        }

        let subcaseId
        let storedDynamicKey

        if (this.selectedChip.value) {
          subcaseId = this.selectedChip.value

          const subcaseToDelete = this.allSubCases.find((subcase) => subcase._id === subcaseId)

          if (!subcaseToDelete) {
            this.$toast.error('Could not find the selected subcase')
            return
          }
          subcaseId = subcaseToDelete._id
          storedDynamicKey = subcaseToDelete[dynamicRequestPath[this.mappedUseCase]?.key]
          // console.log('subcaseToDelete', subcaseToDelete)
        }
        console.log({ subcaseId, storedDynamicKey })

        await this.$axios.delete(`/orgSetup/extend-usecase/${subcaseId}`)

        if (storedDynamicKey && dynamicRequestPath[this.mappedUseCase]?.path) {
          await this.$axios.delete(
            `/orgSetup/${dynamicRequestPath[this.mappedUseCase]?.path}/${storedDynamicKey}`
          )
        }

        await this.fetchAllUseCases()
        this.$toast.success('Subcase deleted successfully')
        this.showDeleteSubCaseDialog = false
        this.selectedChip = null
        this.dataCountResult = null

        // this.$emit('subCases', await this.getUpdatedSubCasesList())
      } catch (error) {
        console.error('Error deleting subcase:', error)
        this.$toast.error(error.response?.data?.message || 'Failed to delete subcase')
      }
    },
    async editItem(chipName, label, indx, key) {
      this.fetchStaticUseCaseData()
      this.openOrgMapping = true
      this.mappedUseCase = key
      this.mappedUseCaseLabel = label
      if (key === 'COLLECTION') {
        for (let i = 0; i < this.enabledSubCases.length; i++) {
          const subCase = this.enabledSubCases[i]
          const selectedSubCase = subCase.subCase.find((item) => item.name === chipName.text)

          // Creating a deep copy of selectedSubCase using Lodash
          if (selectedSubCase) {
            this.editSubCase = { ...cloneDeep(selectedSubCase), key }
            this.editSubCase.indx = indx
          }
        }
        this.mappedUseCaseLabel = defaultOrgKeys
        this.openOrgMapping = true
        this.mappedUseCase = key
        this.$emit('subCases', this.enabledSubCases)
      } else {
        await this.fetchUseCaseById(chipName.value)
      }
    },
    async fetchUseCaseById(id) {
      try {
        const res = await this.$axios.get(`/orgSetup/extend-usecase/${id}`)
        this.editSubCase = res.data?.data
      } catch (error) {
        console.log(error)
      }
    },
    cancel() {
      this.showDeleteSubCaseDialog = false
      this.showDeleteAllDialog = false
      this.selectedChip = null
      this.dataCountResult = null
    },
    handleOrgMapping(key, label) {
      this.fetchStaticUseCaseData()
      this.mappedUseCase = key
      this.mappedUseCaseLabel = label
      if (key === 'COLLECTION') {
        this.mappedUseCaseLabel = defaultOrgKeys
      }
      this.editSubCase = null
      this.openOrgMapping = true
    },
    onCancelOrgMapping() {
      this.openOrgMapping = false
      this.cloneSubCase = {}
      this.editSubCase = {}
    },
    handleSwitch(val) {
      if (val?.id) {
        this.$emit('customerEncrypt', val.value)
      }
    },
    async handleMapKeys(keys) {
      this.cloneSubCase = {}
      this.editSubCase = {}
      await this.fetchAllUseCases()
    },
    async handleSubCaseMapKeys(keys) {
      this.cloneSubCase = {}
      this.editSubCase = {}
      this.enabledSubCases = keys
      this.$emit('subCases', this.enabledSubCases)
    },
    populateFields(val) {
      this.selectedUseCases = val?.enabledUseCases?.map((el) => el.toUpperCase() || [])
      // this.selectedUseCases?.forEach((el) => {
      //   this.use_cases[el.toUpperCase()] = el.toLowerCase()
      // })
      // console.log(this.use_cases, '>>>>>>')
      this.enabledSubCases = val?.enabledSubCases

      // Populate selected icons from received data
      if (val?.useCaseIcons) {
        this.selectedIcons = { ...val.useCaseIcons }
      }
    },
    toggleShowAll() {
      this.drawer = !this.drawer
    }
  },
  async mounted() {
    this.populateFields(this.receivedData)
    this.fetchAllUseCases()
    this.fetchStaticUseCaseData()
    this.fecthAllowedUseCases()
  },
  computed: {
    is_valid_from() {
      return this.$dayjs(this.orgData?.valid_from).format('MMMM D, YYYY')
    },
    is_valid_to() {
      return this.$dayjs(this.orgData?.valid_to).format('MMMM D, YYYY')
    },
    limitations() {
      return this.orgData?.plan_limitations
    },
    planDetails() {
      return this.orgData?.plan_details
    },
    getDeleteConfirmationMessage() {
      if (this.isCheckingCount) {
        return 'Checking for existing data...'
      }

      if (this.dataCountResult && this.dataCountResult.count > 0) {
        return `This use case contains ${this.dataCountResult.count} records. Please migrate the data first or contact your administrator before proceeding with deletion.`
      }

      return 'Are you sure you want to delete this case?'
    },
    filteredIcons() {
      if (!this.iconSearchTerm) {
        return this.apiIcons.slice(0, this.visibleIconsCount)
      }
      const term = this.iconSearchTerm.toLowerCase()
      const filtered = this.apiIcons.filter(
        (icon) =>
          icon.icon.toLowerCase().includes(term) ||
          (icon.text && icon.text.toLowerCase().includes(term))
      )
      return filtered.slice(0, this.visibleIconsCount)
    },
    hasMoreIcons() {
      if (this.iconSearchTerm) return false
      return this.visibleIconsCount < this.apiIcons.length
    },
    filteredUseCases() {
      if (!this.allowedUseCases || this.allowedUseCases.length === 0) {
        return this.use_cases
      }
      const allowedKeys = this.allowedUseCases.map((u) => u.useCase)
      const filtered = {}
      Object.keys(this.use_cases).forEach((key) => {
        if (allowedKeys.includes(key)) {
          filtered[key] = this.use_cases[key]
        }
      })
      return filtered
    }
  }
}
</script>
<style scoped>
.status_color {
  color: #33cc33;
}
.chipContainer {
  display: flex;
  flex-wrap: wrap;
}
.cursor-pointer {
  cursor: pointer;
}
.icon-grid-container {
  max-height: 200px;
  overflow-y: auto;
}
.icon-item:hover {
  background-color: #f5f5f5;
}
.selected-icon {
  background-color: #e3f2fd;
  border: 1px solid #2196f3;
}
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f1f1;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
